import React, { useState, useEffect, useCallback } from "react";
import { auth } from "./firebase";
import { FiCheck } from "react-icons/fi";
import CONFIG from "./config";
import "./QuotaDisplay.css";

function QuotaDisplay() {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add state for modal
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  // Add last refresh timestamp for debouncing
  const [lastRefresh, setLastRefresh] = useState(0);

  // Fetch quota information from the server
  const fetchQuota = useCallback(async (force = false) => {
    // Prevent excessive refreshes unless forced
    const now = Date.now();
    if (!force && now - lastRefresh < 5000) {
      return;
    }
    
    try {
      setLoading(true);
      setLastRefresh(now);
      console.log("Fetching quota information...");
      
      // Force refresh the token to get latest custom claims
      await auth.currentUser.getIdTokenResult(true);
      const token = await auth.currentUser.getIdToken();
      
      const response = await fetch(`${CONFIG.API_URL}/quota`, {
        method: "GET",
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch quota information");
      }
      
      const data = await response.json();
      console.log("Quota data received:", data);
      setQuota(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching quota:", error);
      setError("Failed to load quota information");
      setLoading(false);
    }
  }, [lastRefresh]);

  // Add premium subscription handler
  const handlePremiumSubscription = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const firebaseUid = auth.currentUser.uid;
      const response = await fetch(
        CONFIG.CREATE_CHECKOUT_SESSION,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ firebaseUid }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Subscription error");
      }
      const data = await response.json();
      
      // Store timestamp in localStorage to know when user returns from Stripe
      localStorage.setItem('stripeCheckoutStarted', Date.now().toString());
      
      window.location.href = data.url;
    } catch (error) {
      console.error("Premium subscription error:", error);
      setModalTitle("Premium Subscription Error");
      setModalContent("Subscription failed: " + error.message);
      setShowModal(true);
    }
  };

  // Check if returning from Stripe checkout
  useEffect(() => {
    const checkIfReturningFromStripe = () => {
      const checkoutStarted = localStorage.getItem('stripeCheckoutStarted');
      if (checkoutStarted) {
        const checkoutTime = parseInt(checkoutStarted);
        const now = Date.now();
        // If checkout was started in the last 30 minutes, consider the user returning from Stripe
        if (now - checkoutTime < 30 * 60 * 1000) {
          console.log("User returning from Stripe checkout. Refreshing quota...");
          localStorage.removeItem('stripeCheckoutStarted');
          // Add a slight delay to allow Firebase Auth to refresh
          setTimeout(() => {
            fetchQuota(true);
          }, 2000);
        }
      }
    };
    
    checkIfReturningFromStripe();
  }, [fetchQuota]);

  // Load quota on component mount
  useEffect(() => {
    if (auth.currentUser) {
      fetchQuota();
    }
    
    // Add listener for auth state changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchQuota(true);
      }
    });
    
    return () => unsubscribe();
  }, [fetchQuota]);

  // Modal component
  const Modal = () => {
    if (!showModal) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <h3>{modalTitle}</h3>
          <p>{modalContent}</p>
          <button onClick={() => setShowModal(false)}>Close</button>
        </div>
      </div>
    );
  };

  if (loading || error || !quota) {
    // Return default values if quota data isn't available yet
    return (
      <section className="quota-section">
        <div className="quota-card free-plan">
          <div className="quota-info">
            <h2>Free Plan</h2>
            <p>You have used 0 of 3 weekly job descriptions</p>
            
            <div className="progress-container">
              <div className="progress-bar" style={{ width: '0%' }}></div>
            </div>
            
            <ul className="plan-limits">
              <li><FiCheck /> 3 job descriptions per week</li>
              <li><FiCheck /> 10 resumes per job</li>
            </ul>
          </div>
          
          <div className="upgrade-container">
            <button 
              className="upgrade-button"
              onClick={handlePremiumSubscription}
            >
              Upgrade to Premium
            </button>
            <p className="upgrade-benefits">
              Get 100 rankings per week, more 200 resumes per job, and larger file uploads
            </p>
          </div>
        </div>
        <Modal />
      </section>
    );
  }

  // Get plan details based on tier
  const getPlanDetails = () => {
    const tier = quota.tier || "free";
    console.log("Current user tier:", tier);
    
    switch(tier) {
      case "premium":
        return {
          title: "Pro Plan",
          jobLimit: 100,
          resumesPerJob: 200
        };
      case "enterprise":
        return {
          title: "Enterprise Plan",
          jobLimit: 300,
          resumesPerJob: 500
        };
      default:
        return {
          title: "Free Plan",
          jobLimit: 3,
          resumesPerJob: 5
        };
    }
  };

  const plan = getPlanDetails();
  const jobsUsed = quota.jobs?.used || 0;
  const jobsLimit = quota.jobs?.limit || plan.jobLimit;
  const progressPercent = (jobsUsed / jobsLimit) * 100;
  
  return (
    <section className="quota-section">
      <div className={`quota-card ${quota.tier || 'free'}-plan`}>
        {quota.tier === "premium" && <div className="premium-badge">Premium</div>}
        
        <div className="quota-info">
          <h2>{plan.title}</h2>
          <p>You have used {jobsUsed} of {jobsLimit} weekly job descriptions</p>
          
          <div className="progress-container">
            <div 
              className="progress-bar" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          
          <ul className="plan-limits">
            <li><FiCheck /> {plan.jobLimit} job descriptions per week</li>
            <li><FiCheck /> {plan.resumesPerJob} resumes per job</li>
          </ul>
        </div>
        
        {/* Only show upgrade button for free users */}
        {(!quota.tier || quota.tier === "free") && (
          <div className="upgrade-container">
            <button 
              className="upgrade-button"
              onClick={handlePremiumSubscription}
            >
              Upgrade to Premium
            </button>
            <p className="upgrade-benefits">
              Get 100 rankings per week, more 200 resumes per job, and larger file uploads
            </p>
          </div>
        )}
      </div>
      <Modal />
    </section>
  );
}

export default QuotaDisplay;